import React, { useState } from "react";
import { DeepMap, FieldError, useFormContext } from "react-hook-form";
import { Link } from "react-router-dom";
import { Button, Divider, Form, Icon } from "semantic-ui-react";
import { ControlledInput } from "../ControlledForm";
import queryString from "query-string";
import { OAuthIDP } from "../../hooks/useOAuth";
import { usePublicSSOButtons } from "../../features/auth/hooks/usePublicSSOButtons";

interface IRegisterFormProps {
  site: string;
  onSubmit: (data: any) => void;
  initiateOAuth: (idp: OAuthIDP) => void;
  isLoading?: boolean;
  formErrors: DeepMap<
    {
      firstName: string;
      lastName: string;
      email: string;
      password: string;
    },
    FieldError
  >;
}

export const RegisterForm = ({ site, onSubmit, initiateOAuth, isLoading, formErrors }: IRegisterFormProps) => {
  const { control, handleSubmit } = useFormContext();
  const { redirect } = queryString.parse(window.location.search, { parseBooleans: true });
  const [showPassword, setShowPassword] = useState(false);

  const { hasAnySSO, renderPublicSSOButtons } = usePublicSSOButtons({
    initiateOAuth,
    isLoading,
  });

  const renderIcon = (showPassword: boolean) => (
    <Icon
      name={showPassword ? "eye slash" : "eye"}
      link
      onClick={() => setShowPassword(!showPassword)}
      aria-label={showPassword ? "Hide password" : "Show password"}
      title={showPassword ? "Hide password" : "Show password"}
    />
  );

  return (
    <Form onSubmit={handleSubmit(onSubmit)}>
      <Form.Field required>
        <label htmlFor='firstName'>First Name</label>
        <ControlledInput name='firstName' control={control} autoComplete='given-name' errors={formErrors} />
      </Form.Field>
      <Form.Field required>
        <label htmlFor='lastName'>Last Name</label>
        <ControlledInput name='lastName' control={control} autoComplete='family-name' errors={formErrors} />
      </Form.Field>
      <Form.Field required>
        <label htmlFor='email'>Email</label>
        <ControlledInput name='email' control={control} autoComplete='email' errors={formErrors} />
      </Form.Field>
      <Form.Field required className='tw-mb-2'>
        <label htmlFor='password'>Password</label>
        <ControlledInput
          type={showPassword ? "text" : "password"}
          name='password'
          control={control}
          icon={renderIcon(showPassword)}
          iconPosition='right'
          autoComplete='new-password'
          placeholder='Must be at least 8 characters'
          errors={formErrors}
        />
      </Form.Field>
      <div className='tw-mb-4 tw-flex tw-justify-center'>
        <Button
          className='tw-mt-8 tw-w-[35%] tw-min-w-fit tw-px-6 tw-pb-3 tw-text-lg tw-leading-[1.125]'
          type='submit'
          primary
          size='medium'
          loading={isLoading}
          disabled={isLoading}
        >
          Continue
        </Button>
      </div>
      <div className='tw-text-md tw-p-2 tw-text-center'>
        {`Already have an account? `}
        <Link
          className='tw-font-bold'
          to={`/${site}/public/login${redirect ? "?redirect=" + encodeURIComponent(redirect as string) : ""}`}
        >
          Sign In
        </Link>
      </div>
      {hasAnySSO && (
        <Divider horizontal className='tw-pt-2 tw-font-medium'>
          Or Sign In With
        </Divider>
      )}
      {renderPublicSSOButtons()}
    </Form>
  );
};

export default RegisterForm;
